import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { compose } from 'redux'
import { connect } from 'react-redux'
import { Link, browserHistory } from 'react-router'
import { Card, RenderedHtml } from 'components'
import { Spinner } from 'lp-components'
import { LEARNING_HUB_ROUTE } from 'config'
import * as apiActions from 'api-actions'
import { saveLearningHubBlockProgress } from 'api-actions'
import * as flashActions from 'redux-flash'
import { selectors as globalSelectors } from '../global-reducer'
import { buildInternalApiUrl, getPortalAuthToken } from 'utils'
import {
  getNextBlockEntry,
  blockBelongsToCourseChapter,
  isBlockCompleted,
  isBlockUnlockedInCourse,
  courseCompletionPath,
} from '../utils/block-navigation'
import { courseForumPath } from '../utils/course-forum-url'
import {
  chapterBlockPath as buildChapterBlockPath,
  chapterOverviewPath as buildChapterOverviewPath,
} from '../utils/course-paths'
import { getPreviewTokenForCourse } from '../utils/preview-mode'
import LearningHubPdfBlockPanel from './learning-hub-pdf-block-panel'
import LearningHubFormBlockPanel from './learning-hub-form-block-panel'
import LearningHubVideoBlockPanel from './learning-hub-video-block-panel'
import LearningHubEbookBlockPanel from './learning-hub-ebook-block-panel'
import zoomIcon from 'images/zoom.svg'
import LearningHubBlockTypeIcon from './learning-hub-block-type-icon'

const propTypes = {
  params: PropTypes.shape({
    courseId: PropTypes.string.isRequired,
    chapterId: PropTypes.string.isRequired,
    blockId: PropTypes.string.isRequired,
  }).isRequired,
  currentCourse: PropTypes.object,
  currentBlock: PropTypes.object,
  fetchLearningHubCourse: PropTypes.func.isRequired,
  fetchLearningHubBlock: PropTypes.func.isRequired,
  fetchLearningHubCoursePreview: PropTypes.func.isRequired,
  fetchLearningHubBlockPreview: PropTypes.func.isRequired,
  flashErrorMessage: PropTypes.func.isRequired,
  // Set to true when mounted under /learning-hub/course-preview. In preview
  // mode we (a) authenticate course/block fetches with the per-course JWT
  // stashed in localStorage, (b) skip every saveProgress side effect (no
  // attempt rows to mutate), (c) drop the unlock gate so the admin can hop
  // between blocks freely, and (d) substitute SCORM playback with a
  // placeholder since the SCORM launcher requires a Clearance session.
  isPreviewMode: PropTypes.bool,
}

const defaultProps = {
  currentCourse: undefined,
  currentBlock: undefined,
  isPreviewMode: false,
}

// Format a Zoom meeting/webinar start time + IANA timezone (e.g.
// "America/Los_Angeles") into the design's "Month D, YYYY at H:MM AM/PM TZ"
// form, e.g. "November 18, 2025 at 7:00 PM CT".
function formatZoomDate(value, timeZone) {
  if (!value) return ''
  const date = value instanceof Date ? value : new Date(String(value))
  if (Number.isNaN(date.getTime())) return ''
  const opts = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short',
  }
  if (timeZone) opts.timeZone = timeZone
  try {
    const parts = new Intl.DateTimeFormat('en-US', opts).formatToParts(date)
    const get = (type) => parts.find((p) => p.type === type)?.value || ''
    const month = get('month')
    const day = get('day')
    const year = get('year')
    const hour = get('hour')
    const minute = get('minute')
    const dayPeriod = get('dayPeriod')
    const tz = get('timeZoneName')
    return `${month} ${day}, ${year} at ${hour}:${minute} ${dayPeriod}${
      tz ? ` ${tz}` : ''
    }`
  } catch (err) {
    return date.toLocaleString('en-US', opts)
  }
}

function zoomKindLabel(kind) {
  return String(kind).toLowerCase() === 'webinar' ? 'Webinar' : 'Meeting'
}

function blockHeaderTitle(block) {
  if (block?.blockType === 'zoom' && block?.zoom?.joinUrl) {
    const topic = block.zoom.topic || block.name
    return `${zoomKindLabel(block.zoom.kind)}: ${topic}`
  }
  return block?.name || ''
}

function outlineBlockAttemptStatus(outlineBlock, currentBlock) {
  const fromOutline = outlineBlock.attemptStatus ?? outlineBlock.attempt_status
  if (currentBlock && Number(outlineBlock.id) === Number(currentBlock.id)) {
    return (
      currentBlock.attemptStatus ?? currentBlock.attempt_status ?? fromOutline
    )
  }
  return fromOutline
}

function outlineBlockSidebarCompleted(outlineBlock, currentBlock) {
  return outlineBlockAttemptStatus(outlineBlock, currentBlock) === 'completed'
}

function BlockOutlineIcon({ blockType, completed }) {
  if (completed) {
    return (
      <span
        className="learning-hub-block-player__block-row-icon learning-hub-block-player__block-row-icon--completed"
        aria-hidden
      >
        <svg
          width={22}
          height={22}
          viewBox="0 0 22 22"
          xmlns="http://www.w3.org/2000/svg"
          className="learning-hub-block-player__block-complete-svg"
        >
          <circle cx="11" cy="11" r="11" fill="#087B2E" />
          <path
            d="M6 11.4 L9.6 15 16 8.3"
            fill="none"
            stroke="#fff"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
    )
  }
  if (blockType === 'zoom') {
    return (
      <span
        className="learning-hub-block-player__block-row-icon learning-hub-block-player__block-row-icon--zoom"
        aria-hidden
      >
        <img
          src={zoomIcon}
          alt=""
          width={22}
          height={22}
          className="learning-hub-block-player__block-row-zoom-img"
        />
      </span>
    )
  }
  return (
    <span className="learning-hub-block-player__block-row-icon" aria-hidden>
      <LearningHubBlockTypeIcon
        blockType={blockType}
        className="learning-hub-block-player__block-type-svg"
      />
    </span>
  )
}

BlockOutlineIcon.propTypes = {
  blockType: PropTypes.string,
  completed: PropTypes.bool.isRequired,
}

BlockOutlineIcon.defaultProps = {
  blockType: '',
}

function isPdfBlock(block) {
  return String(block?.blockType || '').toUpperCase() === 'PDF'
}

function isPdfBlockWithViewer(block) {
  return isPdfBlock(block) && Boolean(block?.attachmentUrl)
}

function isEbookBlock(block) {
  return String(block?.blockType || '').toLowerCase() === 'ebook'
}

function isScormBlockWithLauncher(block) {
  const type = String(block?.blockType || '').toLowerCase()
  const launcherPath = block?.scorm?.launcherPath ?? block?.scorm?.launcher_path
  return type === 'scorm_file' && Boolean(launcherPath)
}

function blockSupportsHeaderNextComplete(block) {
  return (
    isPdfBlockWithViewer(block) ||
    isEbookBlock(block) ||
    isScormBlockWithLauncher(block)
  )
}

// Preview-only block renderer.
//
// The full BlockTypePanel below carries a lot of save-progress instrumentation
// (video CMI commits, PDF / e-book completion ticks, form submissions, Zoom
// auto-complete on Join click). All of that requires an authenticated portal
// session and writes real LearningHubBlockAttempt rows — neither of which
// applies in the admin "Preview Course" flow. So in preview we render a
// side-effect-free variant that shows the same content (video, PDF, Zoom
// link, ebook content, form questions) without ever calling saveProgress.
//
// SCORM is special: the SCO needs the server-rendered launcher iframe
// (HTML + API shim + asset proxy) to actually run. We embed it with the
// per-course preview JWT as `?preview_token=<jwt>`, which the launcher
// trades for a signed cookie scoped to /learning_hub/scorm/ — see
// LearningHub::ScormPreviewSession + LearningHub::ScormController#sign_in_via_preview_token!
// The cookie authorises asset + commit_cmi requests inside the iframe
// without ever materialising a learner identity.
function BlockPreviewPanel({ block, previewToken }) {
  const type = String(block?.blockType || '').toLowerCase()
  const desc = block?.description

  if (type === 'video' && block.attachmentUrl) {
    return (
      <div className="learning-hub-block-player__panel">
        {desc ? <RenderedHtml>{desc}</RenderedHtml> : null}
        {/* Bare HTML5 player — no analytics / CMI / progress save. */}
        <video
          src={block.attachmentUrl}
          controls
          className="learning-hub-block-player__preview-video"
          style={{ maxWidth: '100%' }}
        />
      </div>
    )
  }

  if (type === 'pdf' && block.attachmentUrl) {
    return (
      <div className="learning-hub-block-player__panel">
        {desc ? <RenderedHtml>{desc}</RenderedHtml> : null}
        <iframe
          src={block.attachmentUrl}
          title={block.name || 'PDF'}
          className="learning-hub-block-player__preview-pdf"
          style={{ width: '100%', height: '70vh', border: '1px solid #ddd' }}
        />
        <p>
          <a
            href={block.attachmentUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            Open PDF in new tab ↗
          </a>
        </p>
      </div>
    )
  }

  if (type === 'ebook') {
    return (
      <div className="learning-hub-block-player__panel">
        {desc ? <RenderedHtml>{desc}</RenderedHtml> : null}
        {block.ebookContent ? (
          <RenderedHtml>{block.ebookContent}</RenderedHtml>
        ) : (
          <p>E-book content is empty.</p>
        )}
      </div>
    )
  }

  if (type === 'form') {
    const questions = Array.isArray(block.questions) ? block.questions : []
    return (
      <div className="learning-hub-block-player__panel">
        {desc ? <RenderedHtml>{desc}</RenderedHtml> : null}
        <p className="learning-hub-block-player__placeholder-note">
          {`Form preview (${questions.length} question${
            questions.length === 1 ? '' : 's'
          }). Responses are disabled in Course Preview.`}
        </p>
        {questions.length > 0 && (
          <ol className="learning-hub-block-player__preview-form-questions">
            {questions.map((q, idx) => (
              <li key={q.id ?? idx}>{q.prompt || q.label || q.title || ''}</li>
            ))}
          </ol>
        )}
      </div>
    )
  }

  if (type === 'scorm_file') {
    const scormLauncherPath =
      block.scorm?.launcherPath ?? block.scorm?.launcher_path
    if (!scormLauncherPath || !previewToken) {
      return (
        <div className="learning-hub-block-player__panel">
          {desc ? <RenderedHtml>{desc}</RenderedHtml> : null}
          <p className="learning-hub-block-player__placeholder-note">
            SCORM preview is unavailable. Restart the preview from the admin
            Course page to refresh the preview token.
          </p>
        </div>
      )
    }

    const launchUrl = buildInternalApiUrl({
      url: scormLauncherPath,
      query: { preview_token: previewToken },
    })

    return (
      <div className="learning-hub-block-player__panel">
        {desc ? <RenderedHtml>{desc}</RenderedHtml> : null}
        <div className="learning-hub-block-player__scorm">
          <iframe
            src={launchUrl}
            title={block.name || 'SCORM content'}
            className="learning-hub-block-player__scorm-frame"
            allow="autoplay; fullscreen; clipboard-read; clipboard-write"
            allowFullScreen
          />
          <p className="learning-hub-block-player__scorm-actions">
            <a
              href={launchUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="learning-hub-block-player__scorm-fullscreen"
            >
              Open in full screen ↗
            </a>
          </p>
        </div>
      </div>
    )
  }

  if (type === 'zoom' && block.zoom?.joinUrl) {
    const z = block.zoom
    const startTime = z.startTime || block.scheduledTime
    const formattedDate = formatZoomDate(startTime, z.timezone)
    return (
      <div className="learning-hub-block-player__panel">
        {desc ? <RenderedHtml>{desc}</RenderedHtml> : null}
        <div className="learning-hub-block-player__zoom">
          {formattedDate ? (
            <p className="learning-hub-block-player__zoom-date">
              <strong>Date</strong>: {formattedDate}
            </p>
          ) : null}
          {z.password ? (
            <p className="learning-hub-block-player__zoom-passcode">
              <strong>Passcode</strong>: <code>{z.password}</code>
            </p>
          ) : null}
          <p>
            <a
              href={z.joinUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="learning-hub-block-player__zoom-join"
            >
              <img src={zoomIcon} alt="" width={40} height={40} />
              <span className="learning-hub-block-player__zoom-join-label">
                Join Zoom
              </span>
            </a>
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="learning-hub-block-player__panel">
      {desc ? <RenderedHtml>{desc}</RenderedHtml> : null}
      <p className="learning-hub-block-player__placeholder-note">
        Preview is not available for this block.
      </p>
    </div>
  )
}

BlockPreviewPanel.propTypes = {
  block: PropTypes.object.isRequired,
  previewToken: PropTypes.string,
}

BlockPreviewPanel.defaultProps = {
  previewToken: null,
}

function BlockTypePanel({
  block,
  onNavigateAfterSuccess,
  courseId,
  fetchLearningHubCourse,
  fetchLearningHubBlock,
  flashErrorMessage,
  pdfPanelRef,
  ebookPanelRef,
}) {
  const type = block.blockType
  const desc = block.description

  // Tracks block IDs we've already POSTed Zoom completion for, so that
  // re-opening the same Zoom block (or rapidly clicking Join Zoom twice)
  // doesn't fire duplicate progress requests. Server-side is idempotent
  // but we still want to keep the network quiet.
  const zoomJoinPostedRef = useRef(new Set())

  if (type === 'video' && block.attachmentUrl) {
    return <LearningHubVideoBlockPanel block={block} />
  }

  if (type === 'PDF' && block.attachmentUrl) {
    return <LearningHubPdfBlockPanel ref={pdfPanelRef} block={block} />
  }

  if (type === 'form') {
    return (
      <LearningHubFormBlockPanel
        block={block}
        onNavigateAfterSuccess={onNavigateAfterSuccess}
        courseId={courseId}
        fetchLearningHubCourse={fetchLearningHubCourse}
      />
    )
  }

  if (type === 'ebook') {
    return <LearningHubEbookBlockPanel ref={ebookPanelRef} block={block} />
  }

  const scormLauncherPath =
    block.scorm?.launcherPath ?? block.scorm?.launcher_path
  if (type === 'scorm_file' && scormLauncherPath) {
    // Pass the React client's JWT to the iframe so the SCORM controller
    // can establish a session cookie without requiring an admin login.
    // The server consumes the token once and immediately redirects to
    // strip it from the URL.
    const portalToken = getPortalAuthToken()
    const launchUrl = buildInternalApiUrl({
      url: scormLauncherPath,
      query: portalToken ? { t: portalToken } : {},
    })

    return (
      <div className="learning-hub-block-player__panel">
        {desc ? <RenderedHtml>{desc}</RenderedHtml> : null}
        <div className="learning-hub-block-player__scorm">
          <iframe
            src={launchUrl}
            title={block.name || 'SCORM content'}
            className="learning-hub-block-player__scorm-frame"
            allow="autoplay; fullscreen; clipboard-read; clipboard-write"
            allowFullScreen
          />
          <p className="learning-hub-block-player__scorm-actions">
            <a
              href={launchUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="learning-hub-block-player__scorm-fullscreen"
            >
              Open in full screen ↗
            </a>
          </p>
        </div>
      </div>
    )
  }

  if (type === 'zoom' && block.zoom?.joinUrl) {
    const z = block.zoom
    const startTime = z.startTime || block.scheduledTime
    const formattedDate = formatZoomDate(startTime, z.timezone)

    // The Zoom block is unique: there's no auto-detectable "completion"
    // signal (no `onEnded`, no SCORM commit, no form submit). The
    // pragmatic completion signal is the learner clicking Join Zoom —
    // they've engaged with the block. POST the completion in the
    // background so the header "Next" button enables on the very next
    // render (isBlockCompleted(block) flips to true once the refetch
    // resolves), while letting the anchor open Zoom in a new tab as
    // normal (we don't preventDefault).
    const handleJoinZoomClick = () => {
      const posted = zoomJoinPostedRef.current
      if (posted.has(block.id)) return
      posted.add(block.id)

      Promise.resolve()
        .then(() =>
          saveLearningHubBlockProgress(block.id, {
            type: 'zoom',
            progress: 1,
            complete: true,
          })
        )
        .then(() => fetchLearningHubBlock && fetchLearningHubBlock(block.id))
        .then(
          () =>
            fetchLearningHubCourse &&
            courseId &&
            fetchLearningHubCourse(courseId)
        )
        .catch((err) => {
          posted.delete(block.id)
          if (flashErrorMessage) {
            flashErrorMessage(
              err?.message ||
                'Unable to record Zoom progress. Please refresh and try again.'
            )
          }
        })
    }

    return (
      <div className="learning-hub-block-player__panel">
        {desc ? <RenderedHtml>{desc}</RenderedHtml> : null}
        <div className="learning-hub-block-player__zoom">
          {formattedDate ? (
            <p className="learning-hub-block-player__zoom-date">
              <strong>Date</strong>: {formattedDate}
            </p>
          ) : null}
          {z.password ? (
            <p className="learning-hub-block-player__zoom-passcode">
              <strong>Passcode</strong>: <code>{z.password}</code>
            </p>
          ) : null}
          <p>
            <a
              href={z.joinUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="learning-hub-block-player__zoom-join"
              onClick={handleJoinZoomClick}
            >
              <img src={zoomIcon} alt="" width={40} height={40} />
              <span className="learning-hub-block-player__zoom-join-label">
                Join Zoom
              </span>
            </a>
          </p>
        </div>
      </div>
    )
  }

  const label =
    type === 'form'
      ? 'Form'
      : type === 'ebook'
      ? 'E-book'
      : type === 'zoom'
      ? 'Zoom session'
      : type === 'scorm_file'
      ? 'SCORM package'
      : 'This block type'

  return (
    <div className="learning-hub-block-player__panel">
      {desc ? <RenderedHtml>{desc}</RenderedHtml> : null}
      <p className="learning-hub-block-player__placeholder-note">
        {`${label} player is not available in this preview yet.`}
      </p>
      {type === 'zoom' && block.scheduledTime ? (
        <p>
          <strong>Scheduled:</strong> {String(block.scheduledTime)}
        </p>
      ) : null}
    </div>
  )
}

BlockTypePanel.propTypes = {
  block: PropTypes.object.isRequired,
  onNavigateAfterSuccess: PropTypes.func.isRequired,
  courseId: PropTypes.number.isRequired,
  fetchLearningHubCourse: PropTypes.func.isRequired,
  fetchLearningHubBlock: PropTypes.func,
  flashErrorMessage: PropTypes.func,
  pdfPanelRef: PropTypes.shape({ current: PropTypes.any }),
  ebookPanelRef: PropTypes.shape({ current: PropTypes.any }),
}

BlockTypePanel.defaultProps = {
  fetchLearningHubBlock: undefined,
  flashErrorMessage: undefined,
  pdfPanelRef: undefined,
  ebookPanelRef: undefined,
}

function ChapterBlockPlayerPage({
  params,
  currentCourse,
  currentBlock,
  fetchLearningHubCourse,
  fetchLearningHubBlock,
  fetchLearningHubCoursePreview,
  fetchLearningHubBlockPreview,
  flashErrorMessage,
  isPreviewMode,
}) {
  const courseId = Number(params.courseId)
  const chapterId = Number(params.chapterId)
  const blockId = Number(params.blockId)

  const [expandedChapterIds, setExpandedChapterIds] = useState(() => new Set())
  const [nextBusy, setNextBusy] = useState(false)
  const pdfPanelRef = useRef(null)
  const ebookPanelRef = useRef(null)
  const prevBlockAttemptRef = useRef(null)

  // Preview-mode fetches use the per-course JWT stashed in localStorage by
  // CoursePreviewShell. If the admin reloaded /course-preview/course/.../blocks/...
  // directly without coming through the entry shell, the token may be
  // missing; the preview API calls will then 401 and the player renders an
  // empty state until they restart the preview flow.
  const previewToken = isPreviewMode ? getPreviewTokenForCourse(courseId) : null

  useEffect(() => {
    if (Number.isNaN(courseId)) return
    if (isPreviewMode) {
      if (!previewToken) return
      fetchLearningHubCoursePreview(courseId, previewToken)
      return
    }
    fetchLearningHubCourse(courseId)
  }, [
    courseId,
    isPreviewMode,
    previewToken,
    fetchLearningHubCourse,
    fetchLearningHubCoursePreview,
  ])

  useEffect(() => {
    // Attempt-status tracking is a learner concern; preview has no attempts.
    if (isPreviewMode) return
    if (
      Number.isNaN(blockId) ||
      Number.isNaN(courseId) ||
      !currentBlock ||
      Number(currentBlock.id) !== blockId
    ) {
      return
    }
    const status = currentBlock.attemptStatus ?? currentBlock.attempt_status
    const prev = prevBlockAttemptRef.current
    if (
      prev &&
      prev.blockId === currentBlock.id &&
      prev.status !== 'completed' &&
      status === 'completed'
    ) {
      fetchLearningHubCourse(courseId)
    }
    prevBlockAttemptRef.current = { blockId: currentBlock.id, status }
  }, [
    isPreviewMode,
    blockId,
    courseId,
    currentBlock?.id,
    currentBlock?.attemptStatus,
    currentBlock?.attempt_status,
    fetchLearningHubCourse,
  ])

  useEffect(() => {
    if (Number.isNaN(blockId)) return
    if (isPreviewMode) {
      if (!previewToken) return
      fetchLearningHubBlockPreview(blockId, previewToken)
      return
    }
    fetchLearningHubBlock(blockId)
  }, [
    blockId,
    isPreviewMode,
    previewToken,
    fetchLearningHubBlock,
    fetchLearningHubBlockPreview,
  ])

  useEffect(() => {
    if (!Number.isNaN(chapterId)) {
      setExpandedChapterIds(new Set([chapterId]))
    }
  }, [chapterId, blockId])

  const course =
    currentCourse && Number(currentCourse.id) === courseId
      ? currentCourse
      : undefined
  const block =
    currentBlock && Number(currentBlock.id) === blockId
      ? currentBlock
      : undefined

  const chapters = useMemo(() => course?.chapters || [], [course])

  const outlineValid = useMemo(
    () =>
      course &&
      block &&
      blockBelongsToCourseChapter(course, chapterId, blockId) &&
      Number(block.chapterId) === chapterId,
    [course, block, chapterId, blockId]
  )

  const blockReachable = useMemo(() => {
    if (!outlineValid || !course) return null
    // Admins driving a Course Preview shouldn't be gated by attempt history.
    if (isPreviewMode) return true
    return isBlockUnlockedInCourse(course, blockId)
  }, [outlineValid, course, blockId, isPreviewMode])

  useEffect(() => {
    if (blockReachable !== false) return
    browserHistory.replace(
      buildChapterOverviewPath(courseId, { isPreview: isPreviewMode })
    )
  }, [blockReachable, courseId, isPreviewMode])

  const nextEntry = useMemo(
    () => (course ? getNextBlockEntry(course, blockId) : null),
    [course, blockId]
  )
  const headerNextCompleteBlock = blockSupportsHeaderNextComplete(block)

  // PDF, e-book, and SCORM: header Next stays enabled so learners can mark complete via Next.
  // Other block types: Next requires the current block attempt to be completed.
  // In preview mode Next is always enabled — admins are touring the player,
  // not tracking real completion — so they can hop forward at will.
  const headerNextEnabled = useMemo(() => {
    if (isPreviewMode) return Boolean(nextEntry)
    if (headerNextCompleteBlock) return true
    if (!nextEntry) return false
    return isBlockCompleted(block)
  }, [isPreviewMode, headerNextCompleteBlock, nextEntry, block])

  const showHeaderNext = Boolean(
    isPreviewMode
      ? nextEntry
      : nextEntry || (headerNextCompleteBlock && !isBlockCompleted(block))
  )

  // Learner-only: certificate generation belongs to a real attempt history.
  // In preview we render a "Back to chapters" button instead (see JSX below).
  const showCompleteCourse = useMemo(
    () =>
      Boolean(
        course &&
          block &&
          !nextEntry &&
          !isPreviewMode &&
          isBlockCompleted(block)
      ),
    [course, block, nextEntry, isPreviewMode]
  )

  const showPreviewBackToChapters = Boolean(
    isPreviewMode && course && block && !nextEntry
  )

  const completeBlockViaHeaderNextIfNeeded = async () => {
    // Preview never writes attempt rows; advance straight to the next block
    // without touching the panel ref methods (which would saveProgress).
    if (isPreviewMode) return
    if (!headerNextCompleteBlock || isBlockCompleted(block)) return

    if (isPdfBlockWithViewer(block)) {
      const pdfApi = pdfPanelRef.current
      if (!pdfApi?.completeAndAdvance) {
        throw new Error('PDF viewer is not ready.')
      }
      await pdfApi.completeAndAdvance()
    } else if (isEbookBlock(block)) {
      const ebookApi = ebookPanelRef.current
      if (!ebookApi?.completeAndAdvance) {
        throw new Error('E-book viewer is not ready.')
      }
      await ebookApi.completeAndAdvance()
    } else if (isScormBlockWithLauncher(block)) {
      await saveLearningHubBlockProgress(block.id, {
        type: 'scorm_file',
        progress: 1,
        complete: true,
      })
    }

    await fetchLearningHubCourse(courseId)
    await fetchLearningHubBlock(blockId)
  }

  const toggleChapter = (id) => {
    setExpandedChapterIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const goToBlock = (chId, blkId) => {
    browserHistory.push(
      buildChapterBlockPath(courseId, chId, blkId, { isPreview: isPreviewMode })
    )
  }

  const navigateAfterFormSuccess = useCallback(async () => {
    await fetchLearningHubCourse(courseId)
    if (nextEntry) {
      goToBlock(nextEntry.chapterId, nextEntry.block.id)
    } else {
      browserHistory.push(courseCompletionPath(courseId))
    }
  }, [courseId, fetchLearningHubCourse, nextEntry])

  if (!course || !block) {
    return (
      <div className="content-block-container learning-hub-block-player">
        <Spinner />
      </div>
    )
  }

  if (!outlineValid) {
    return (
      <div className="content-block-container learning-hub-block-player">
        <Card>
          <h1>Block not found</h1>
          <p>This block does not belong to the selected chapter or course.</p>
          <Link
            to={buildChapterOverviewPath(courseId, {
              isPreview: isPreviewMode,
            })}
          >
            Back to chapters
          </Link>
        </Card>
      </div>
    )
  }

  if (blockReachable === false) {
    return (
      <div className="content-block-container learning-hub-block-player">
        <Spinner />
      </div>
    )
  }

  return (
    <div className="content-block-container learning-hub-block-player">
      {isPreviewMode && (
        <div className="learning-hub-preview-banner" role="status">
          <p>You are viewing a Course Preview. Block progress is not saved.</p>
        </div>
      )}
      {!isPreviewMode && (
        <nav
          className="learning-hub-block-player__breadcrumb"
          aria-label="Breadcrumb"
        >
          <Link to={`${LEARNING_HUB_ROUTE}/course-catalog`}>
            Course Catalog
          </Link>
          <span>/</span>
          <span>{course.title}</span>
          <span>/</span>
          <Link to={`${LEARNING_HUB_ROUTE}/course/${courseId}/chapters`}>
            Chapters
          </Link>
          <span>/</span>
          <span>{block.name}</span>
        </nav>
      )}

      <header className="learning-hub-block-player__header">
        <h1>{course.title}</h1>
        <p>{course.instructor}</p>
      </header>

      <div className="learning-hub-block-player__grid">
        <Card className="learning-hub-block-player__sidebar">
          <h3 className="learning-hub-block-player__sidebar-title">
            {course.title}
          </h3>
          {chapters.map((chapter, chIdx) => {
            const expanded = expandedChapterIds.has(Number(chapter.id))
            const blocks = [...(chapter.blocks || [])].sort((a, b) => {
              const oa = Number(a.order) || 0
              const ob = Number(b.order) || 0
              if (oa !== ob) return oa - ob
              return Number(a.id) - Number(b.id)
            })
            return (
              <div
                key={chapter.id}
                className={`learning-hub-block-player__chapter-wrap${
                  expanded ? ' is-expanded' : ''
                }`}
              >
                <button
                  type="button"
                  className="learning-hub-block-player__chapter-toggle"
                  aria-expanded={expanded}
                  onClick={() => toggleChapter(Number(chapter.id))}
                >
                  <span>{`Chapter ${chIdx + 1}`}</span>
                  <span
                    className="learning-hub-block-player__chapter-chevron"
                    aria-hidden
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width={10}
                      height={6}
                      viewBox="0 0 10 6"
                      fill="none"
                    >
                      <path
                        d="M5 6L0 1.13514L1.16667 0L5 3.72973L8.83333 0L10 1.13514L5 6Z"
                        fill="currentColor"
                      />
                    </svg>
                  </span>
                </button>
                {expanded && (
                  <ul className="learning-hub-block-player__block-list">
                    {blocks.map((b) => (
                      <li key={b.id}>
                        <button
                          type="button"
                          className={`learning-hub-block-player__block-row ${
                            Number(b.id) === blockId ? 'is-active' : ''
                          }`}
                          onClick={() => goToBlock(chapter.id, b.id)}
                        >
                          <BlockOutlineIcon
                            blockType={b.blockType}
                            completed={outlineBlockSidebarCompleted(b, block)}
                          />
                          <span className="learning-hub-block-player__block-row-text">
                            <span className="learning-hub-block-player__block-row-title">
                              {b.title}
                            </span>
                            <span className="learning-hub-block-player__block-row-meta">
                              {`${b.estimatedTime || 0} min`}
                            </span>
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )
          })}

          {!isPreviewMode && (
            <button
              type="button"
              className="learning-hub-block-player__forum-button"
              onClick={() => browserHistory.push(courseForumPath(course))}
            >
              Go to Course Forum
            </button>
          )}
        </Card>

        <section className="learning-hub-block-player__main">
          <div className="learning-hub-block-player__lesson-card">
            <div className="learning-hub-block-player__gradient-bar">
              <h2>{blockHeaderTitle(block)}</h2>
              {showHeaderNext ? (
                <button
                  type="button"
                  className={`learning-hub-block-player__next ${
                    headerNextEnabled && !nextBusy ? '' : 'is-disabled'
                  }`}
                  disabled={
                    headerNextCompleteBlock
                      ? nextBusy
                      : !headerNextEnabled || nextBusy
                  }
                  onClick={async () => {
                    if (nextBusy) return
                    if (!headerNextCompleteBlock && !headerNextEnabled) return
                    setNextBusy(true)
                    try {
                      await completeBlockViaHeaderNextIfNeeded()
                    } catch (err) {
                      flashErrorMessage(
                        err?.message ||
                          'Unable to save progress before continuing.'
                      )
                      return
                    } finally {
                      setNextBusy(false)
                    }
                    if (nextEntry) {
                      goToBlock(nextEntry.chapterId, nextEntry.block.id)
                      return
                    }
                    if (headerNextCompleteBlock) {
                      browserHistory.push(courseCompletionPath(courseId))
                    }
                  }}
                >
                  Next
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="15"
                    height="15"
                    viewBox="0 0 15 15"
                    fill="none"
                  >
                    <path
                      d="M7.5 0L6.15937 1.30594L11.3906 6.5625H0V8.4375H11.3906L6.15937 13.6622L7.5 15L15 7.5L7.5 0Z"
                      fill="black"
                    />
                  </svg>
                </button>
              ) : showCompleteCourse ? (
                <button
                  type="button"
                  className="learning-hub-block-player__next"
                  onClick={() =>
                    browserHistory.push(courseCompletionPath(courseId))
                  }
                >
                  Complete course
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="15"
                    height="15"
                    viewBox="0 0 15 15"
                    fill="none"
                    aria-hidden
                  >
                    <path
                      d="M7.5 0L6.15937 1.30594L11.3906 6.5625H0V8.4375H11.3906L6.15937 13.6622L7.5 15L15 7.5L7.5 0Z"
                      fill="black"
                    />
                  </svg>
                </button>
              ) : showPreviewBackToChapters ? (
                <button
                  type="button"
                  className="learning-hub-block-player__next"
                  onClick={() =>
                    browserHistory.push(
                      buildChapterOverviewPath(courseId, { isPreview: true })
                    )
                  }
                >
                  Back to chapters
                </button>
              ) : (
                <span className="learning-hub-block-player__next is-disabled">
                  Next
                </span>
              )}
            </div>

            {isPreviewMode ? (
              <BlockPreviewPanel block={block} previewToken={previewToken} />
            ) : (
              <BlockTypePanel
                block={block}
                onNavigateAfterSuccess={navigateAfterFormSuccess}
                courseId={courseId}
                fetchLearningHubCourse={fetchLearningHubCourse}
                fetchLearningHubBlock={fetchLearningHubBlock}
                flashErrorMessage={flashErrorMessage}
                pdfPanelRef={pdfPanelRef}
                ebookPanelRef={ebookPanelRef}
              />
            )}
          </div>
        </section>
      </div>
    </div>
  )
}

ChapterBlockPlayerPage.propTypes = propTypes
ChapterBlockPlayerPage.defaultProps = defaultProps

function mapStateToProps(state) {
  return {
    currentCourse: globalSelectors.currentCourse(state),
    currentBlock: globalSelectors.currentBlock(state),
  }
}

const mapDispatchToProps = {
  fetchLearningHubCourse: apiActions.fetchLearningHubCourse,
  fetchLearningHubBlock: apiActions.fetchLearningHubBlock,
  fetchLearningHubCoursePreview: apiActions.fetchLearningHubCoursePreview,
  fetchLearningHubBlockPreview: apiActions.fetchLearningHubBlockPreview,
  flashErrorMessage: flashActions.flashErrorMessage,
}

export default compose(connect(mapStateToProps, mapDispatchToProps))(
  ChapterBlockPlayerPage
)
